import { supabase } from "@/platform/lib/supabase";
import { readErrorFromResponse } from "@/platform/billing/errors";

export interface UspDiscursiveQuestion {
  id: string;
  year: number;
  number: number;
  day: number | null;
  discipline: string;
  stem: string;
  expected_answer: string | null;
  alternatives: { items?: Array<{ id: string; sub_question: string }> } | null;
  exam_paper_id: string | null;
  page_number: number | null;
  has_graph: boolean;
  has_image: boolean;
  has_table: boolean;
}

export interface GradeDiscursive2aResult {
  transcription?: string;
  items_scores: Array<{
    id: string;
    score: number;
    max_score: number;
    feedback: string;
    lacuna_identificada?: string;
  }>;
  total_score: number;
  max_score: number;
  predominant_error_type: string;
  feedback: string;
  next_steps: string[];
  expected_answer_comments?: string;
  graded_without_reference?: boolean;
  confidence: number;
  attempt_id: string | null;
  source: "text" | "image";
}

/** Disciplinas que realmente têm discursivas de 2ª fase no corpus, ordenadas por volume. */
export async function listUspDiscursiveDisciplines(): Promise<string[]> {
  const { data, error } = await supabase
    .from("usp_questions")
    .select("discipline")
    .eq("kind", "discursive")
    .eq("phase", "2");
  if (error) throw error;
  const counts = new Map<string, number>();
  for (const r of (data ?? []) as Array<{ discipline: string }>) {
    counts.set(r.discipline, (counts.get(r.discipline) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([d]) => d);
}

export async function listUspDiscursives(discipline?: string): Promise<UspDiscursiveQuestion[]> {
  let query = supabase
    .from("usp_questions")
    .select(
      "id, year, number, day, discipline, stem, expected_answer, alternatives, exam_paper_id, page_number, has_graph, has_image, has_table",
    )
    .eq("kind", "discursive")
    .eq("phase", "2")
    .order("year", { ascending: false });
  if (discipline) query = query.eq("discipline", discipline);
  const { data, error } = await query;
  if (error) throw error;
  return (data as UspDiscursiveQuestion[]) ?? [];
}

export async function uploadUspDiscursiveImages(params: {
  userId: string;
  uuid: string;
  files: File[];
}): Promise<string[]> {
  const paths: string[] = [];
  for (let i = 0; i < params.files.length; i++) {
    const file = params.files[i];
    if (!file) continue;
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `${params.userId}/discursive/${params.uuid}/page-${i + 1}.${ext}`;
    const { error } = await supabase.storage
      .from("usp-essays")
      .upload(path, file, { contentType: file.type, upsert: true });
    if (error) throw new Error(`upload ${path}: ${error.message}`);
    paths.push(path);
  }
  return paths;
}

/**
 * Storage path (bucket usp-question-assets) da página renderizada do enunciado,
 * para questões com gráfico/tabela/figura. A function anexa essa imagem ao
 * Gemini para não corrigir visual às cegas. Retorna null se não houver render.
 */
export async function fetchQuestionImagePath(
  examPaperId: string | null,
  pageNumber: number | null,
): Promise<string | null> {
  if (!examPaperId || pageNumber == null) return null;
  const { data, error } = await supabase
    .from("usp_paper_pages")
    .select("storage_path")
    .eq("exam_paper_id", examPaperId)
    .eq("page_number", pageNumber)
    .maybeSingle();
  if (error || !data) return null;
  return (data as { storage_path: string }).storage_path;
}

export async function gradeDiscursive2aFase(payload: {
  discipline: string;
  question_id?: string;
  session_id?: string;
  stem: string;
  items?: Array<{ id: string; sub_question: string }>;
  expected_approach?: string;
  student_answer?: string;
  image_paths?: string[];
  question_image_path?: string;
}): Promise<GradeDiscursive2aResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error("Sessão expirada. Entre novamente.");

  const res = await fetch("/api/grade-discursive-2afase", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw await readErrorFromResponse(res);
  }
  return (await res.json()) as GradeDiscursive2aResult;
}
